import AES from 'crypto-js/aes';
import encUtf8 from 'crypto-js/enc-utf8';
import Swal from 'sweetalert2';
import { NotificationManager } from '../components/common/react-notifications';
import {
  authUserKey,
  authorizationsKey,
  defaultColor,
  defaultDirection,
  defaultLocale,
  defaultRadius,
  directionKey,
  groupsKey,
  localeKey,
  localeOptions,
  navigationMenuKey,
  notificationsKey,
  pendingNotificationsKey,
  portfolioFiltersParametersKey,
  portfolioFiltersShowPhotographKey,
  reportFiltersGroupsCategoriesIdsKey,
  reportFiltersRangesdatesKey,
  reportFiltersUsersIdsKey,
  reportFiltersUsersStatusKey,
  themeColorStorageKey,
  themeRadiusStorageKey,
  valuesSystemKey,
  widgetsKey,
  widgetsSectionsColumnsKey,
  projectsKey,
  homeFiltersDatesKey,
  myNegotiationsTypesFiltersFieldOrderKey,
  authEntityKey,
  progressFiltersParametersKey,
  changeReportFiltersUsersStatusKey,
  changeFinalizedPlanningStatusKey,
  monitorsFiltersKey,
} from '../constants/defaultValues';
import MainServices from '../services/MainServices';
import GeneralsHelper from './generalsHelper';
import { FilterUserEnum, PeriodsChartEnum, QuestionEnum, StatusFinishedEnum, validityStatusEnum } from './enums';

//#region Functionality
export const mapOrder = (array, order, key) => {
  array.sort((a, b) => {
    const A = a[key];
    const B = b[key];
    if (order.indexOf(`${A}`) > order.indexOf(`${B}`)) {
      return 1;
    }
    return -1;
  });
  return array;
};

export const getDateWithFormat = () => {
  const today = new Date();
  let dd = today.getDate();
  let mm = today.getMonth() + 1; // January is 0!

  const yyyy = today.getFullYear();
  if (dd < 10) {
    dd = `0${dd}`;
  }
  if (mm < 10) {
    mm = `0${mm}`;
  }
  return `${dd}.${mm}.${yyyy}`;
};

export const getCurrentTime = () => {
  const now = new Date();
  return `${now.getHours()}:${now.getMinutes()}`;
};
//#endregion

//#region Template
export const getDirection = () => {
  let direction = defaultDirection;

  try {
    if (localStorage.getItem(directionKey)) {
      let localValue = localStorage.getItem(directionKey);
      localValue = DecryptData(direction);
      if (localValue === 'rtl' || localValue === 'ltr') {
        direction = localValue;
      }
    }
  } catch (error) {
    direction = defaultDirection;
  }
  return {
    direction,
    isRtl: direction === 'rtl',
  };
};

export const setDirection = (localValue) => {
  let direction = 'ltr';
  if (localValue === 'rtl' || localValue === 'ltr') {
    direction = localValue;
  }
  try {
    let dataDirection = EncryptData(direction);
    localStorage.setItem(directionKey, dataDirection);
  } catch (error) {
    localStorage.removeItem(directionKey);
  }
};

export const getCurrentColor = () => {
  let currentColor = defaultColor;
  try {
    if (localStorage.getItem(themeColorStorageKey)) {
      currentColor = localStorage.getItem(themeColorStorageKey);
      currentColor = DecryptData(currentColor);
    }
  } catch (error) {
    currentColor = defaultColor;
  }
  return currentColor;
};

export const setCurrentColor = (color) => {
  try {
    let dataColor = EncryptData(color);
    localStorage.setItem(themeColorStorageKey, dataColor);
  } catch (error) {
    localStorage.removeItem(themeColorStorageKey);
  }
};

export const getCurrentRadius = () => {
  let currentRadius = defaultRadius;
  try {
    if (localStorage.getItem(themeRadiusStorageKey)) {
      currentRadius = localStorage.getItem(themeRadiusStorageKey);
      currentRadius = DecryptData(currentRadius);
    }
  } catch (error) {
    currentRadius = defaultRadius;
  }
  return currentRadius;
};

export const setCurrentRadius = (radius) => {
  try {
    let dataRadius = EncryptData(radius);
    localStorage.setItem(themeRadiusStorageKey, dataRadius);
  } catch (error) {
    localStorage.removeItem(themeRadiusStorageKey);
  }
};

export const getCurrentLanguage = () => {
  let language = defaultLocale;
  try {
    if (localStorage.getItem(localeKey)) {
      language = localStorage.getItem(localeKey);
      language = DecryptData(language);
    }
    language = localeOptions.filter((x) => x.id === language).length > 0 ? language : defaultLocale;
  } catch (error) {
    language = defaultLocale;
  }
  return language;
};

export const setCurrentLanguage = (locale) => {
  try {
    let dataLocale = EncryptData(locale);
    localStorage.setItem(localeKey, dataLocale);
  } catch (error) {
    localStorage.removeItem(localeKey);
  }
};

export const getCurrentValuesSystem = () => {
  let currentValuesSystem = [];
  try {
    if (localStorage.getItem(valuesSystemKey)) {
      currentValuesSystem = localStorage.getItem(valuesSystemKey);
      currentValuesSystem = DecryptData(currentValuesSystem);
    }
  } catch (error) {
    currentValuesSystem = [];
  }
  return currentValuesSystem;
};

export const setCurrentValuesSystem = (valuesSystem) => {
  try {
    let dataValuesSystem = EncryptData(valuesSystem);
    localStorage.setItem(valuesSystemKey, dataValuesSystem);
  } catch (error) {
    localStorage.removeItem(valuesSystemKey);
  }
};

export const getCurrentUser = () => {
  let user = null;
  try {
    user = localStorage.getItem('gogo_current_user') != null ? JSON.parse(localStorage.getItem('gogo_current_user')) : null;
  } catch (error) {
    // console.log('>>>>: src/helpers/Utils.js  : getCurrentUser -> error', error);
    user = null;
  }
  return user;
};

export const setCurrentUser = (user) => {
  try {
    if (user) {
      localStorage.setItem('gogo_current_user', JSON.stringify(user));
    } else {
      localStorage.removeItem('gogo_current_user');
    }
  } catch (error) {
    // console.log('>>>>: src/helpers/Utils.js : setCurrentUser -> error', error);
  }
};
//#endregion

//#region Notifications
export const showNotification = (alert) => {
  if (alert.messages && alert?.messages?.length > 0) {
    if (alert?.type) sweetAlertsList(alert.title, alert.messages, alert.type);
    else sweetAlertsListError(alert.title, alert.messages);
    return;
  }
  switch (alert?.type) {
    case 'success':
      NotificationManager.success(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
    case 'error':
      NotificationManager.error(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
    case 'warning':
      NotificationManager.warning(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
    case 'info':
      NotificationManager.info(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
    case 'primary':
      NotificationManager.primary(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
    case 'secondary':
      NotificationManager.secondary(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
    case 'change':
      NotificationManager.change(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
    default:
      NotificationManager.info(
        alert?.message,
        alert?.title,
        alert?.duration,
        alert?.handleActionAlert,
        alert?.priority,
        alert?.className,
      );
      break;
  }
};

export const showDialogLoading = (intl, message) => {
  Swal.fire({
    title: intl.formatMessage({
      id: 'sweetAlert.title',
    }),
    html: `${message} <br> ${intl.formatMessage({
      id: 'sweetAlert.message-wait',
    })}`,
    timerProgressBar: true,
    allowOutsideClick: false,
    customClass: {
      container: 'my-swal',
    },
    didOpen: () => {
      Swal.showLoading();
    },
  });
};

export const sweetAlertsListError = (title, messages) => {
  if (Array.isArray(messages) && messages.length > 0) {
    Swal.fire({
      icon: 'error',
      title: `${title}!`,
      html: messages.join('<br>'),
    });
  }
};

export const sweetAlertsList = (title, messages, type = 'info') => {
  if (Array.isArray(messages) && messages.length > 0) {
    Swal.fire({
      icon: type,
      title: title,
      html: messages.join('<br>'),
    });
  }
};

export const dismissDialogLoading = (message) => {
  Swal.close();
};
//#endregion

//#region Redux Auth
export const getAuthUser = () => {
  let currentAuthUser = null;
  try {
    if (localStorage.getItem(authUserKey)) {
      currentAuthUser = localStorage.getItem(authUserKey);
      currentAuthUser = DecryptData(currentAuthUser);
    }
  } catch (error) {
    currentAuthUser = null;
  }
  return currentAuthUser;
};

export const setAuthUser = (user) => {
  try {
    let dataUser = EncryptData(user);
    localStorage.setItem(authUserKey, dataUser);
  } catch (error) {
    localStorage.removeItem(authUserKey);
  }
};

//#region Redux Entity
export const getAuthEntity = () => {
  let currentAuthEntity = null;
  try {
    if (localStorage.getItem(authEntityKey)) {
      currentAuthEntity = localStorage.getItem(authEntityKey);
      currentAuthEntity = DecryptData(currentAuthEntity);
    }
  } catch (error) {
    currentAuthEntity = null;
  }
  return currentAuthEntity;
};

export const setAuthEntity = (entity) => {
  try {
    let dataEntity = EncryptData(entity);
    localStorage.setItem(authEntityKey, dataEntity);
  } catch (error) {
    localStorage.removeItem(authEntityKey);
  }
};
//#endregion
export const getAuthorizations = () => {
  let currentAuthorizations = null;
  try {
    if (localStorage.getItem(authorizationsKey)) {
      currentAuthorizations = localStorage.getItem(authorizationsKey);
      currentAuthorizations = DecryptData(currentAuthorizations);
    }
  } catch (error) {
    currentAuthorizations = null;
  }
  return currentAuthorizations;
};

export const setAuthorizations = (authorizations) => {
  try {
    let dataAuthorizations = EncryptData(authorizations);
    localStorage.setItem(authorizationsKey, dataAuthorizations);
  } catch (error) {
    localStorage.removeItem(authorizationsKey);
  }
};

export const getNavigationMenu = () => {
  let currentNavigationMenu = null;
  try {
    if (localStorage.getItem(navigationMenuKey)) {
      currentNavigationMenu = localStorage.getItem(navigationMenuKey);
      currentNavigationMenu = DecryptData(currentNavigationMenu);
    }
  } catch (error) {
    currentNavigationMenu = null;
  }
  return currentNavigationMenu;
};

export const setNavigationMenu = (navigationMenu) => {
  try {
    let dataNavigationMenu = EncryptData(navigationMenu);
    localStorage.setItem(navigationMenuKey, dataNavigationMenu);
  } catch (error) {
    localStorage.removeItem(navigationMenuKey);
  }
};

export const getWidgets = () => {
  let currentWidgets = [];
  try {
    if (localStorage.getItem(widgetsKey)) {
      currentWidgets = localStorage.getItem(widgetsKey);
      currentWidgets = DecryptData(currentWidgets);
    }
  } catch (error) {
    currentWidgets = [];
  }
  return currentWidgets;
};

export const setWidgets = (widgets) => {
  try {
    let dataWidgets = EncryptData(widgets);
    localStorage.setItem(widgetsKey, dataWidgets);
  } catch (error) {
    localStorage.removeItem(widgetsKey);
  }
};

export const getGroups = () => {
  let currentGroups = [];
  try {
    if (localStorage.getItem(groupsKey)) {
      currentGroups = localStorage.getItem(groupsKey);
      currentGroups = DecryptData(currentGroups);
    }
  } catch (error) {
    currentGroups = [];
  }
  return currentGroups;
};

export const setGroups = (groups) => {
  try {
    let dataGroups = EncryptData(groups);
    localStorage.setItem(groupsKey, dataGroups);
  } catch (error) {
    localStorage.removeItem(groupsKey);
  }
};

export const getSectionsColumns = () => {
  let currentSectionsColumns = null;
  try {
    if (localStorage.getItem(widgetsSectionsColumnsKey)) {
      currentSectionsColumns = localStorage.getItem(widgetsSectionsColumnsKey);
      currentSectionsColumns = DecryptData(currentSectionsColumns);
    }
  } catch (error) {
    currentSectionsColumns = null;
  }
  return currentSectionsColumns;
};

export const setSectionsColumns = (sectionsColumns) => {
  try {
    let dataSectionsColumnsWidgets = EncryptData(sectionsColumns);
    localStorage.setItem(widgetsSectionsColumnsKey, dataSectionsColumnsWidgets);
  } catch (error) {
    localStorage.removeItem(widgetsSectionsColumnsKey);
  }
};
export const setProjects = (projects) => {
  try {
    let dataProjects = EncryptData(projects);
    localStorage.setItem(projectsKey, dataProjects);
  } catch (error) {
    localStorage.removeItem(projectsKey);
  }
};
export const getProjects = () => {
  let currentProjects = null;
  try {
    if (localStorage.getItem(projectsKey)) {
      currentProjects = localStorage.getItem(projectsKey);
      currentProjects = DecryptData(currentProjects);
    }
  } catch (error) {
    currentProjects = null;
  }
  return currentProjects;
};
//#endregion

//#region Redux Notification
export const getNotifications = () => {
  let currentNotifications = [];
  try {
    if (localStorage.getItem(authUserKey)) {
      currentNotifications = localStorage.getItem(notificationsKey);
      currentNotifications = DecryptData(currentNotifications);
    }
  } catch (error) {
    currentNotifications = [];
  }
  return currentNotifications;
};

export const setNotifications = (notifications) => {
  try {
    let dataNotifications = EncryptData(notifications);
    localStorage.setItem(notificationsKey, dataNotifications);
  } catch (error) {
    localStorage.removeItem(notificationsKey);
  }
};

export const getPendingNotifications = () => {
  let currentPendingNotifications = 0;
  try {
    if (localStorage.getItem(authUserKey)) {
      currentPendingNotifications = localStorage.getItem(pendingNotificationsKey);
      currentPendingNotifications = DecryptData(currentPendingNotifications);
    }
  } catch (error) {
    currentPendingNotifications = 0;
  }
  return currentPendingNotifications;
};

export const setPendingNotifications = (pendingNotifications) => {
  try {
    let dataPendingNotifications = EncryptData(pendingNotifications);
    localStorage.setItem(pendingNotificationsKey, dataPendingNotifications);
  } catch (error) {
    localStorage.removeItem(pendingNotificationsKey);
  }
};
//#endregion

//#region Filters Reports
export const setReportFiltersUsersIds = (usersIds) => {
  try {
    let dataUsersIds = EncryptData(usersIds);
    localStorage.setItem(reportFiltersUsersIdsKey, dataUsersIds);
  } catch (error) {
    localStorage.removeItem(reportFiltersUsersIdsKey);
  }
};

export const getReportFiltersUsersIds = () => {
  let usersIds = [];
  try {
    usersIds = localStorage.getItem(reportFiltersUsersIdsKey);
    usersIds = DecryptData(usersIds);
  } catch (error) {
    usersIds = [];
  }
  return usersIds ?? [];
};
export const setReportFiltersUsersStatus = (usersIds) => {
  try {
    let dataUsersIds = EncryptData(usersIds);
    localStorage.setItem(reportFiltersUsersStatusKey, dataUsersIds);
  } catch (error) {
    localStorage.removeItem(reportFiltersUsersStatusKey);
  }
};

export const getReportFiltersUsersStatus = () => {
  let usersIds = null;
  try {
    usersIds = localStorage.getItem(reportFiltersUsersStatusKey);
    usersIds = DecryptData(usersIds);
  } catch (error) {
    usersIds = 'ACTIVE';
  }
  return usersIds ?? 'ACTIVE';
};

export const setReportFiltersRangeDate = (rangeDate) => {
  try {
    let dataRangeDate = EncryptData(rangeDate);
    localStorage.setItem(reportFiltersRangesdatesKey, dataRangeDate);
  } catch (error) {
    localStorage.removeItem(reportFiltersRangesdatesKey);
  }
};

export const getReportFiltersRangeDate = () => {
  let dataRangeDate = null;
  try {
    dataRangeDate = localStorage.getItem(reportFiltersRangesdatesKey);
    dataRangeDate = DecryptData(dataRangeDate);
  } catch (error) {
    dataRangeDate = null;
  }
  return dataRangeDate;
};

export const setReportFiltersGroupsCategoriesIds = (groupsCategoriesIds) => {
  try {
    let dataGroupsCategoriesIds = EncryptData(groupsCategoriesIds);
    localStorage.setItem(reportFiltersGroupsCategoriesIdsKey, dataGroupsCategoriesIds);
  } catch (error) {
    localStorage.removeItem(reportFiltersGroupsCategoriesIdsKey);
  }
};

export const getReportFiltersGroupsCategoriesIds = () => {
  let groupsCategoriesIds = [];
  try {
    groupsCategoriesIds = localStorage.getItem(reportFiltersGroupsCategoriesIdsKey);
    groupsCategoriesIds = DecryptData(groupsCategoriesIds);
  } catch (error) {
    groupsCategoriesIds = [];
  }
  return groupsCategoriesIds ?? [];
};
//#endregion

/// PORTAFOLIO
export const setPortfolioFiltersShowPhotograph = (showPhotograph) => {
  try {
    let dataShowPhotograph = EncryptData(showPhotograph);
    localStorage.setItem(portfolioFiltersShowPhotographKey, dataShowPhotograph);
  } catch (error) {
    localStorage.removeItem(portfolioFiltersShowPhotographKey);
  }
};
export const getPortfolioFiltersShowPhotograph = () => {
  let showPhotograph = QuestionEnum.NO;
  try {
    showPhotograph = localStorage.getItem(portfolioFiltersShowPhotographKey);
    showPhotograph = DecryptData(showPhotograph);
  } catch (error) {
    showPhotograph = QuestionEnum.NO;
  }
  return showPhotograph ?? QuestionEnum.NO;
};
export const setPortfolioFiltersParameters = (parameters) => {
  try {
    let dataParameters = EncryptData(parameters);
    localStorage.setItem(portfolioFiltersParametersKey, dataParameters);
  } catch (error) {
    localStorage.removeItem(portfolioFiltersParametersKey);
  }
};
export const setProgressFiltersParameters = (parameters) => {
  try {
    let dataParameters = EncryptData(parameters);
    localStorage.setItem(progressFiltersParametersKey, dataParameters);
  } catch (error) {
    localStorage.removeItem(progressFiltersParametersKey);
  }
};
export const setHomeFiltersDates = (parameters) => {
  try {
    let dataParameters = EncryptData(parameters);
    localStorage.setItem(homeFiltersDatesKey, dataParameters);
  } catch (error) {
    localStorage.removeItem(homeFiltersDatesKey);
  }
};
export const getPortfolioFiltersParameters = () => {
  const baseParameters = {
    negotiations_types_id: [],
    negotiations_types_statuses_id: [],
    batches_id: [],
    searchText: '',
    entityListType: '',
    entities_type_id: 0,
    filterDynamics: [],
  };

  let parameters = baseParameters;
  try {
    parameters = localStorage.getItem(portfolioFiltersParametersKey);
    parameters = DecryptData(parameters);
  } catch (error) {
    parameters = baseParameters;
  }
  return parameters ?? baseParameters;
};
export const getHomeFiltersDates = () => {
  const dateObject = GeneralsHelper.getDateByType(PeriodsChartEnum.CURRENT_MONTH);
  const baseParameters = {
    label: dateObject?.type?.label,
    value: dateObject?.type?.value,
    type: dateObject?.type?.value,
    startDate: dateObject?.startDate.format('yyyy-MM-DD') ?? '',
    endDate: dateObject?.endDate.format('yyyy-MM-DD') ?? '',
  };
  let parameters = baseParameters;
  try {
    parameters = localStorage.getItem(homeFiltersDatesKey);
    parameters = DecryptData(parameters);
  } catch (error) {
    parameters = baseParameters;
  }
  return parameters ?? baseParameters;
};
//#endregion

//#region Methods Security APP
export const RemoveCredentialsApp = () => {
  localStorage.removeItem(authUserKey);
  localStorage.removeItem(authorizationsKey);
  localStorage.removeItem(navigationMenuKey);
  localStorage.removeItem(notificationsKey);
  localStorage.removeItem(widgetsKey);
  localStorage.removeItem(widgetsSectionsColumnsKey);
  localStorage.removeItem(pendingNotificationsKey);
  localStorage.removeItem(groupsKey);
  localStorage.removeItem(themeRadiusStorageKey);
  localStorage.removeItem(themeColorStorageKey);
  localStorage.removeItem(reportFiltersGroupsCategoriesIdsKey);
  localStorage.removeItem(reportFiltersUsersIdsKey);
  localStorage.removeItem(reportFiltersRangesdatesKey);
  window.location.href = window.location.origin;
};
export const RemoveConfigurations = () => {
  localStorage.removeItem(reportFiltersGroupsCategoriesIdsKey);
  localStorage.removeItem(reportFiltersUsersIdsKey);
  localStorage.removeItem(reportFiltersRangesdatesKey);
  localStorage.removeItem(reportFiltersUsersIdsKey);
};
export const EncryptData = (textToEncrypt) => {
  let textEncrypt = null;
  try {
    if (textToEncrypt && typeof textToEncrypt === 'object') {
      textEncrypt = AES.encrypt(JSON.stringify(textToEncrypt), MainServices.KEY_ENCRIPT_STORAGE);
    } else {
      textEncrypt = AES.encrypt(textToEncrypt, MainServices.KEY_ENCRIPT_STORAGE);
    }
  } catch (error) {
    textEncrypt = null;
  }
  return textEncrypt;
};

export const DecryptData = (textEncrypted) => {
  let textDecrypt = null;
  try {
    textDecrypt = AES.decrypt(textEncrypted, MainServices.KEY_ENCRIPT_STORAGE).toString(encUtf8);
    if (GeneralsHelper.IsJsonString(textDecrypt)) {
      textDecrypt = JSON.parse(textDecrypt);
    }
  } catch (error) {
    textDecrypt = null;
  }
  return textDecrypt;
};

export const setGlobalValue = (key, value) => {
  window[key] = value;
};

export const getGlobalValue = (key) => {
  return window[key];
};
export const setMyNegotiationsTypesFiltersFieldOrder = (fieldOrder) => {
  try {
    let dataParameters = EncryptData(fieldOrder);
    localStorage.setItem(myNegotiationsTypesFiltersFieldOrderKey, dataParameters);
  } catch (error) {
    localStorage.removeItem(myNegotiationsTypesFiltersFieldOrderKey);
  }
};
export const getMyNegotiationsTypesFiltersFieldOrder = () => {
  try {
    let parameters = localStorage.getItem(myNegotiationsTypesFiltersFieldOrderKey);
    parameters = DecryptData(parameters);
    return parameters ?? null;
  } catch (error) {
    return null;
  }
};
export const getProgressFiltersParameters = () => {
  const dateObject = GeneralsHelper.getDateByType(PeriodsChartEnum.CURRENT_MONTH_TODAY);
  const rangeDate = {
    type: dateObject?.type ?? null,
    startDate: dateObject?.startDate.format('yyyy-MM-DD') ?? '',
    endDate: dateObject?.endDate.format('yyyy-MM-DD') ?? '',
  };
  const baseParameters = {
    range_date: rangeDate,
    users_id: [],
    groups_categories_id: [],
    filterUserEnum: FilterUserEnum.ACTIVE,
    batches_id: [],
    validity: validityStatusEnum.ALL,
  };

  let parameters = baseParameters;
  try {
    parameters = localStorage.getItem(progressFiltersParametersKey);
    parameters = DecryptData(parameters);
  } catch (error) {
    parameters = baseParameters;
  }
  return parameters ?? baseParameters;
};

export const setChangeFinalizedPlanningStatusKey = (statuses) => {
  try {
    // Valores por defecto si no se pasa nada o si el array está vacío
    const defaultValues = [
      StatusFinishedEnum.NOT_FINISHED,
      StatusFinishedEnum.FINISHED_INCOMPLETE,
      StatusFinishedEnum.FINISHED_COMPLETE,
    ];

    const valueToSave = Array.isArray(statuses) && statuses.length > 0 ? statuses : defaultValues;

    const encrypted = EncryptData(valueToSave);
    localStorage.setItem(changeFinalizedPlanningStatusKey, encrypted);
  } catch (error) {
    console.error('Error saving finalized planning status:', error);
    localStorage.removeItem(changeFinalizedPlanningStatusKey);
  }
};
export const getChangeFinalizedPlanningStatusKey = () => {
  try {
    const stored = localStorage.getItem(changeFinalizedPlanningStatusKey);
    if (!stored) return [];

    const decrypted = DecryptData(stored);
    return Array.isArray(decrypted) ? decrypted : [];
  } catch (error) {
    console.error('Error reading finalized planning status:', error);
    return [];
  }
};

export const setMonitorsFilters = (rangeDate) => {
  try {
    let data = EncryptData(rangeDate);
    localStorage.setItem(monitorsFiltersKey, data);
  } catch (error) {
    localStorage.removeItem(monitorsFiltersKey);
  }
};

export const getMonitorsFilters = () => {
  let data = null;

  try {
    data = localStorage.getItem(monitorsFiltersKey);
    data = DecryptData(data);
  } catch (error) {
    data = null;
  }
  return data;
};
//#endregion
//#region Methods Security APP
export const RemoveCredentialsEntity = () => {
  localStorage.removeItem(authEntityKey);
  window.location.href = `${window.location.origin}/entity/login`;
};

//#endregion
